import classNames from 'classnames'
import { useState, useMemo } from 'react'
import { AutoScroll } from '~/components/ui/AutoScroll'
import { MyIcon } from '~/components/ui/MyIcon'
import {
  getGeneratorTags_aiArtworkResourceModule_spellTab,
  getGeneratorTags_aiArtworkResourceModule_spellTab_tags,
} from '~/graphqls/types'
import { strCutout } from '~/utils'
import styles from './TagTab.module.css'

export interface TagsTypes
  extends getGeneratorTags_aiArtworkResourceModule_spellTab_tags {
  weight: number
}

/** 标签列表 */
const TagList = ({
  activeTabTags,
  selectList,
  setSelectList,
  englishVisible,
  onMinus,
  onPlus,
}: {
  activeTabTags: getGeneratorTags_aiArtworkResourceModule_spellTab[]
  selectList: TagsTypes[]
  setSelectList: React.Dispatch<React.SetStateAction<TagsTypes[]>>
  [key: string]: any
}) => {
  return (
    <div className="flex justify-start items-center flex-wrap gap-2">
      {activeTabTags?.map((it: any) => {
        const active = !!selectList?.filter(_it => _it.id === it.id)?.length
        return (
          <div
            className={classNames(styles.tag, {
              active: active,
            })}
            key={it.id}
          >
            {active && (
              <div className="tagMinus" onClick={() => onMinus(it.id)}>
                <MyIcon icon="minus" className="text-[16px]" />
              </div>
            )}
            <div
              className="tagName"
              onClick={() => {
                if (active) {
                  setSelectList(list => list.filter(val => val.id !== it.id))
                  return
                }
                setSelectList((list: any) => [...list, { ...it, weight: 0 }])
              }}
            >
              {it.name}
              {englishVisible && ` ${strCutout(it.englishName, 25)}`}
            </div>
            {active && (
              <div className="tagPlus" onClick={() => onPlus(it.id)}>
                <MyIcon icon="plus" className="text-[16px]" />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** 标签分组聚合 */
const DEFAULT_GROUP_NAME = '11!@#$%*&({|}|)!@&*&%$#^*()_+-?><>,.'
const formatTags = (tags: TagsTypes[]): [any[], boolean] => {
  if (!tags) {
    return [[], false]
  }
  const result: any[] = []
  let hasGroup = false
  let defaultGroup: any
  tags.forEach((item: any) => {
    if (item.spellLabel) {
      // 存在分组
      hasGroup = true
      const group = result.find(
        subItem => subItem.spellLabel === item.spellLabel
      )
      if (group) {
        group.children.push(item)
      } else {
        result.push({
          spellLabel: item.spellLabel,
          children: [item],
        })
      }
      return
    }
    // 其它分组
    if (!defaultGroup) {
      defaultGroup = {
        spellLabel: DEFAULT_GROUP_NAME,
        children: [item],
      }
    } else {
      defaultGroup.children.push(item)
    }
  })
  if (defaultGroup) {
    result.push(defaultGroup)
  }
  return [result, hasGroup]
}

export const TagTab = ({
  tagsData,
  selectList,
  setSelectList,
  englishVisible = false,
}: {
  tagsData: getGeneratorTags_aiArtworkResourceModule_spellTab[]
  selectList: TagsTypes[]
  setSelectList: React.Dispatch<React.SetStateAction<TagsTypes[]>>
  englishVisible?: boolean
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const onMinus = (id: number) => {
    setSelectList(list => {
      const _list = list.map(it => {
        if (id === it.id) {
          return {
            ...it,
            weight: (it.weight || 0) - 1,
          }
        } else {
          return it
        }
      })
      return _list
    })
  }

  const onPlus = (id: number) => {
    setSelectList(list => {
      const _list = list.map(it => {
        if (id === it.id) {
          return {
            ...it,
            weight: (it.weight || 0) + 1,
          }
        } else {
          return it
        }
      })
      return _list
    })
  }

  // 标签二级细分
  const [activeTabTagGroups, hasGroup] = useMemo(() => {
    // 根据分组进行拆分
    return formatTags(tagsData?.[activeIndex]?.tags as TagsTypes[])
  }, [activeIndex])

  return (
    <div
      className="mt-4 rounded-xl overflow-hidden min-h-[464px] border-2 border-neutral-100"
      style={{
        boxShadow: `0px 1px 4px rgba(255, 255, 255, 0.08), 0px 0px 16px rgba(255, 255, 255, 0.08)`,
      }}
    >
      <AutoScroll activeIndex={activeIndex} className="bg-neutral-200">
        <div
          className={classNames(
            'flex justify-start items-center mx-auto',
            styles.tabItems
          )}
        >
          {tagsData?.map((it, index) => {
            const ids = selectList?.map(it => it.id)
            const len =
              it.tags?.filter(tag => ids.includes(tag.id))?.length || 0
            return (
              <div
                key={it.id}
                className={classNames(
                  styles.tabItem,
                  'flex-shrink-0 relative',
                  {
                    active: activeIndex === index,
                  }
                )}
                onClick={() => setActiveIndex(index)}
              >
                <>
                  {it.name}
                  {!!len && (
                    <span className="text-xs leading-none px-1 py-0.5 bg-error rounded-full text-neutral-800 absolute top-2 right-2">
                      {len}
                    </span>
                  )}
                </>
              </div>
            )
          })}
        </div>
      </AutoScroll>

      <div className="p-4">
        {activeTabTagGroups?.map(item => (
          <div className={styles.tagGroup} key={item.spellLabel}>
            {hasGroup && (
              <>
                {item.spellLabel === DEFAULT_GROUP_NAME ? (
                  <div className="py-1" />
                ) : (
                  <div className={styles.tagGroupWrap}>
                    <h3 className={styles.tagGroupName}>{item.spellLabel}</h3>
                    <div className={styles.tagGroupLine} />
                  </div>
                )}
              </>
            )}
            <TagList
              selectList={selectList}
              setSelectList={setSelectList}
              activeTabTags={item.children}
              englishVisible={englishVisible}
              onMinus={onMinus}
              onPlus={onPlus}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
